import { useTranslation } from "react-i18next";
import { formatPrice } from "@/core/utils/format-price";
import type { Order } from "../../models/order";
import type { VoucherDistribution } from "../../models/voucher-distribution";
import type { ConfigName } from "../../types/config-name";
import { VoucherConfigs } from "../voucher-configs";

type ThankYouProps = {
  voucherDistribution: VoucherDistribution;
  vouchers: Order["items"];
  configName: ConfigName;
  totalGrossPrice: string | number;
  showCurrentConfig?: boolean;
};

export const ThankYou = ({
  voucherDistribution,
  vouchers,
  configName,
  totalGrossPrice,
  showCurrentConfig,
}: ThankYouProps) => {
  const { t } = useTranslation();
  const otherConfigs = voucherDistribution?.configs.filter(
    (config) => showCurrentConfig || config.configName !== configName,
  );

  return (
    <div data-testid="thank-you-page">
      <div className="mb-12">
        <h1 className="mb-4 text-center font-serif text-xl lg:text-4xl">
          {t(`cmsTranslations.${configName}.thank_you_page.title`)}
        </h1>
        <p className="mx-auto max-w-xl text-center">{t(`cmsTranslations.${configName}.thank_you_page.description`)}</p>
      </div>
      <div className="mx-auto max-w-2xl rounded-xl bg-white p-6 shadow-md lg:p-10">
        <h3 className="mb-6 font-serif text-xl lg:text-2xl">{t("VOUCHERS_DISTRIBUTION.THANK_YOU.YOUR_ORDER")}</h3>
        <div className="mx-auto flex w-full flex-col items-center justify-center gap-4">
          {vouchers.map((voucher) => (
            <div
              key={voucher.productUuid}
              className="flex w-full items-center justify-between rounded-xl border bg-gray-50 bg-opacity-50 p-4"
            >
              <span className="font-semibold text-lg">
                {voucher.quantity > 1 ? `${voucher.quantity}x ` : ""}
                {voucher.name}
              </span>
              <span className="font-medium">{formatPrice(Number(voucher.grossPrice) * voucher.quantity)}</span>
            </div>
          ))}
          <div className="my-2 h-px w-full bg-gray-200" />
          <div className="flex w-full items-center justify-between px-4">
            <span className="font-semibold text-lg">{t("VOUCHERS_DISTRIBUTION.THANK_YOU.TOTAL_PAID")}</span>
            <span className="font-bold text-xl">{formatPrice(totalGrossPrice)}</span>
          </div>
        </div>
      </div>
      <VoucherConfigs configs={otherConfigs} configName={configName} />
    </div>
  );
};
